// covered-by: frontends/carrie/src/ask-v2/review/__tests__/AskV2ReviewCommentsList.test.tsx
import { Badge, Text } from "@radix-ui/themes";
import { type ReactNode, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  CollapsedCardDetails,
  CommentIdentityRow,
  CommentSeverityBadge,
  FoldedIntoSubheading,
  reviewCommentPermissions,
  JudgmentReceiptLink,
  SpecQuoteLine,
} from "./AskV2ReviewCommentCardParts";
import {
  AskV2ReviewCommentEditor,
  AskV2ReviewOwnCommentActions,
} from "./AskV2ReviewCommentEditor";
import { AskV2ReviewCommentJudgmentActions } from "./AskV2ReviewCommentJudgmentActions";
import { AskV2ReviewCommentRowActions } from "./AskV2ReviewCommentRowActions";
import { AskV2ReviewCompanionRow } from "./AskV2ReviewCompanionRow";
import {
  PreviouslyConfirmedSubheading,
  PreviouslyRejectedSubheading,
} from "./AskV2ReviewJudgmentReceipt";
import { AskV2ReviewRejectedHereSubheading } from "./AskV2ReviewRejectedHereSubheading";
import {
  AskV2ReviewRejectScopePrompt,
  type RejectionChoice,
} from "./AskV2ReviewRejectScopePrompt";
import { CommentTagBadges } from "./CommentTagBadges";
import { severityPinTone } from "./pinGroups";
import { type CommentWithOptionalTags } from "./reviewCommentTags";

import {
  AskPunttReviewCommentBody,
  AskPunttReviewCommentSource,
  type AssetFile,
  reviewCommentAuthorLabel,
  reviewCommentTitle,
} from "../../components/ask-puntt-review";
import { type ReviewCommentAuthorOptions } from "../../components/ask-puntt-review/reviewCommentDisplay";
import { type LinkableClaim } from "../../plugins/rehype-claim-citation";
import { type LinkableMemory } from "../../utils/memoryCitationFields";
import {
  isRejectedBefore,
  reviewCommentGroup,
} from "../../utils/visibleReviewComments";

/** Saves an edit of a comment's text; rejects when christo did not keep it. */
export type AskV2ReviewCommentEditHandler = (
  commentId: string,
  description: string,
) => Promise<unknown>;

/** Soft-deletes the caller's own human comment; rejects when christo did not. */
export type AskV2ReviewCommentDeleteHandler = (
  commentId: string,
) => Promise<unknown>;

export type AskV2ReviewCommentCardProps = {
  comment: CommentWithOptionalTags;
  commentNumber: number;
  expanded: boolean;
  /** Typography findings on this finding's spot, drawn inside its card. */
  companions?: readonly CommentWithOptionalTags[];
  /** The finding whose delete opened the reject-scope prompt, so a companion's opens in its row. */
  rejectingId?: string | null;
  /** This finding's header delete opened the scope prompt. */
  rejecting: boolean;
  authorOptions: ReviewCommentAuthorOptions;
  claimCitations?: LinkableClaim[];
  memoryCitations?: LinkableMemory[];
  contextFiles: readonly AssetFile[];
  reviewFiles: readonly AssetFile[];
  authorNameByUserId?: Readonly<Record<string, string>>;
  currentUserId?: string;
  currentUserName?: string;
  judgmentPending: boolean;
  onJudge?: (
    commentId: string,
    liked: boolean | null,
    rejection?: RejectionChoice,
  ) => void;
  /** Absent for read-only viewers; withheld, no card offers Edit. */
  onEdit?: AskV2ReviewCommentEditHandler;
  /** Absent for read-only viewers; withheld, no card offers Delete. */
  onDelete?: AskV2ReviewCommentDeleteHandler;
  onSelect: (commentId: string) => void;
  onClear: () => void;
  onRejectingChange: (commentId: string | null) => void;
  /** Relevant / Not relevant under the row, in place of the hover heart and delete. */
  rowActions?: boolean;
  /**
   * Collapsed, the card shows only its byline, title and risk chip; the
   * Issue / Fix / Spec body waits for the drawer (flag `ask-v2-review-comment-drawer`).
   */
  summary?: boolean;
  /** Drawn left of the card: the bulk selection box. */
  leading?: ReactNode;
};

export function AskV2ReviewCommentCard({
  comment,
  commentNumber,
  expanded,
  companions,
  rejectingId = null,
  rejecting,
  authorOptions,
  claimCitations,
  memoryCitations,
  contextFiles,
  reviewFiles,
  authorNameByUserId,
  currentUserId,
  currentUserName,
  judgmentPending,
  onJudge,
  onEdit,
  onDelete,
  onSelect,
  onClear,
  onRejectingChange,
  rowActions = false,
  summary = false,
  leading,
}: AskV2ReviewCommentCardProps) {
  const { t } = useTranslation("ui");
  const id = comment._id;
  // The editor's state: the draft it opens with, and whether the last save
  // failed. `editing` null means the editor is closed.
  const [editing, setEditing] = useState<{
    draft: string;
    failed: boolean;
  } | null>(null);
  // why: closing the card (another pin, or the header) abandons an edit that
  // was never saved, rather than leaving a hidden editor to reappear later.
  useEffect(() => {
    if (!expanded) setEditing(null);
  }, [expanded]);

  const title = reviewCommentTitle(comment);
  const collapsedSurface =
    "bg-puntt-neutral-gray-3 hover:bg-base-white transition-colors";
  // why: the row actions sit outside the header button, so a button with its
  // own hover stayed gray while the card around it went white. It follows the card.
  const collapsedButtonSurface =
    "bg-puntt-neutral-gray-3 group-hover/comment:bg-base-white transition-colors";
  const expandedSurface = "bg-base-white";
  // why: a demoted finding is one she already threw out. Dimmed and
  // dashed while collapsed so the list separates it at a glance (it is
  // also sorted last); full strength on hover or when opened, because
  // demote-not-drop means it must stay readable, never hidden. A finding
  // she rejects in this review gets the same look under Rejected and keeps
  // its number, so Restore is one click on the card away.
  // A finding she restored here is live again, so it loses the dim.
  const demoted = isRejectedBefore(comment) && comment.restoredAt == null;
  const rejectedHere = comment.liked === false;
  const demotedSurface =
    (demoted || rejectedHere) && !expanded
      ? " border-dashed opacity-60 hover:opacity-100"
      : "";
  const { canJudge, canEdit, canDelete } = reviewCommentPermissions({
    comment,
    currentUserId,
    onJudge,
    onEdit,
    onDelete,
  });
  const showEditButton = canEdit && editing == null;
  const showDeleteButton = canDelete && editing == null;
  const ownActionCount = (showEditButton ? 1 : 0) + (showDeleteButton ? 1 : 0);
  const headerJudge = canJudge && !rowActions;
  const actionCount = headerJudge ? 2 : ownActionCount;
  const rejected = reviewCommentGroup(comment) === "rejected";
  const authorLabel = reviewCommentAuthorLabel(comment, authorOptions);
  const edited = !comment.isAI && comment.editedAt != null;
  const rejectedHereLabel = (
    <AskV2ReviewRejectedHereSubheading
      comment={comment}
      commentId={id}
      authorNameByUserId={authorNameByUserId}
      currentUserId={currentUserId}
      currentUserName={currentUserName}
    />
  );
  const subheadings = (
    <>
      <Text
        as="p"
        size="3"
        weight="medium"
        className="leading-snug text-puntt-neutral-gray-12"
        data-testid={`ask-v2-review-comment-title-${id}`}
      >
        {title}
      </Text>
      {rejectedHereLabel}
      <PreviouslyRejectedSubheading
        comment={comment}
        commentId={id}
        currentUserId={currentUserId}
      />
      <PreviouslyConfirmedSubheading
        comment={comment}
        commentId={id}
        currentUserId={currentUserId}
      />
      <FoldedIntoSubheading comment={comment} commentId={id} />
    </>
  );

  const startEdit = () => {
    onRejectingChange(null);
    setEditing({ draft: comment.description, failed: false });
    if (!expanded) onSelect(id);
  };
  const saveEdit = (description: string) => {
    // The new text is already on screen (the hook patches the cache before
    // the PUT), so the editor closes at once. A failure rolls the text back
    // and reopens the editor with her draft, so nothing she typed is lost.
    setEditing(null);
    onEdit?.(id, description).catch(() => {
      setEditing({ draft: description, failed: true });
    });
  };

  return (
    <li className={leading ? "mb-2 flex items-start gap-2" : "mb-2"}>
      {leading}
      <div
        className={`group/comment relative min-w-0 flex-1 overflow-hidden rounded-lg border border-puntt-neutral-gray-5 transition-opacity ${
          expanded ? expandedSurface : collapsedSurface
        }${demotedSurface}`}
        data-testid={`ask-v2-review-comment-card-${id}`}
        data-demoted={demoted ? "true" : undefined}
        data-rejected={rejectedHere ? "true" : undefined}
      >
        {/* why: the header action icons render as a sibling (absolute, top-right),
            never inside this button -- a button inside a button is invalid
            markup and swallows the inner click. */}
        <button
          type="button"
          className={`w-full p-3 text-left ${
            expanded ? expandedSurface : collapsedButtonSurface
          }`}
          data-testid={`ask-v2-review-comment-${id}`}
          aria-expanded={expanded}
          onClick={() => {
            onRejectingChange(null);
            if (expanded) onClear();
            else onSelect(id);
          }}
        >
          {expanded ? (
            <>
              <CommentIdentityRow
                id={id}
                commentNumber={commentNumber}
                tone={severityPinTone(comment)}
                authorLabel={authorLabel}
                createdAt={comment.createdAt}
                edited={edited}
                actionCount={actionCount}
              />
              <div className="mt-2 min-w-0 text-left">
                {subheadings}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {comment.isRequired ? (
                    <Badge
                      size="1"
                      variant="solid"
                      color="blue"
                      data-testid="ask-v2-review-comment-urgent-badge"
                    >
                      {t("askV2.review.urgentBadge")}
                    </Badge>
                  ) : null}
                  <CommentSeverityBadge severity={comment.severity} />
                  <CommentTagBadges comment={comment} commentId={id} />
                </div>
              </div>
            </>
          ) : (
            <div className="line-clamp-5 text-left">
              <CommentIdentityRow
                id={id}
                commentNumber={commentNumber}
                tone={severityPinTone(comment)}
                authorLabel={authorLabel}
                createdAt={comment.createdAt}
                edited={edited}
                actionCount={actionCount}
              />
              <div className="mt-2 min-w-0">{subheadings}</div>
              <CollapsedCardDetails comment={comment} summary={summary} />
            </div>
          )}
        </button>
        {canJudge && rowActions ? (
          <div className="px-3 pb-3">
            <AskV2ReviewCommentRowActions
              commentId={id}
              liked={comment.liked}
              rejected={rejected}
              disabled={judgmentPending}
              onJudge={(liked, rejection) => onJudge?.(id, liked, rejection)}
            />
          </div>
        ) : null}
        {expanded ? (
          <div
            className="bg-base-white px-3 pb-3 pt-0"
            data-testid={`ask-v2-review-comment-expanded-${id}`}
          >
            <JudgmentReceiptLink comment={comment} commentId={id} />
            {canEdit && editing != null ? (
              <AskV2ReviewCommentEditor
                commentId={id}
                saved={comment.description}
                initialDraft={editing.draft}
                failed={editing.failed}
                onSave={saveEdit}
                onCancel={() => setEditing(null)}
              />
            ) : (
              <AskPunttReviewCommentBody
                commentId={id}
                description={comment.description}
                claimCitations={claimCitations}
                memoryCitations={memoryCitations}
                afterExpand={
                  <>
                    <SpecQuoteLine comment={comment} />
                    <AskPunttReviewCommentSource
                      comment={comment}
                      contextFiles={contextFiles}
                      reviewFiles={reviewFiles}
                    />
                  </>
                }
              />
            )}
            {headerJudge && rejecting ? (
              <AskV2ReviewRejectScopePrompt
                commentId={id}
                onConfirm={(choice) => {
                  onRejectingChange(null);
                  onJudge?.(id, false, choice);
                }}
                onCancel={() => onRejectingChange(null)}
              />
            ) : null}
          </div>
        ) : null}
        {companions?.map((companion) => (
          <AskV2ReviewCompanionRow
            key={companion._id}
            comment={companion}
            rejecting={rejectingId === companion._id}
            judgmentPending={judgmentPending}
            onRejectingChange={onRejectingChange}
            onJudge={onJudge}
          />
        ))}
        {headerJudge ? (
          <div className="absolute right-3 top-3.5">
            <AskV2ReviewCommentJudgmentActions
              commentId={id}
              liked={comment.liked}
              rejected={rejected}
              disabled={judgmentPending}
              pinned={expanded || comment.liked != null || rejected}
              onJudge={(liked) => {
                onRejectingChange(null);
                onJudge?.(id, liked);
              }}
              onRequestDelete={() => {
                onRejectingChange(id);
                if (!expanded) onSelect(id);
              }}
            />
          </div>
        ) : null}
        <AskV2ReviewOwnCommentActions
          commentId={id}
          pinned={expanded}
          onDelete={showDeleteButton ? onDelete : undefined}
          onRequestEdit={showEditButton ? startEdit : undefined}
        />
      </div>
    </li>
  );
}
